import { backgroundGet, foregroundGet } from "@/api/base/private/backgroundClient";
import {
  dropLiveRoomsCache,
  fetchLiveRooms,
  readRoomState,
  sweepAssignment,
  sweepLiveRooms,
  type LiveRoom,
  type RoomSweep,
} from "./janusSweep";
import {
  rememberExam,
  rememberStudent,
} from "@/app/[value]/realtime/attemptIndex";
import {
  ATTEMPT_LIST_FIELDS,
  ATTEMPT_OWNER_FIELDS,
  fetchStudentAttempts as queryStudentAttempts,
} from "@/app/[value]/realtime/attemptsQuery";

export interface StudentHit {
  id: number;
  userId: number | null;
  name: string;
  detail: string | null;
  photo: string | null;
  groups: string[];
  email: string | null;
  phone: string | null;
  externalId: string | null;
  registerDate: string | null;
  lastActivityDate: string | null;
  credibility?: number | null;
  attemptId?: number | null;
  attemptCredibility?: number | null;
}

export interface AssignmentHit {
  id: number;
  name: string;
  status: string | null;
  isProctoring: boolean;
  studentsCount: number | null;
  startingAt: string | null;
  webinarId: number | null;
}

export interface UserHit {
  id: number;
  name: string;
  detail: string | null;
  photo: string | null;
  role: string | null;
  studentId: number | null;
}

export interface GroupHit {
  id: number;
  name: string;
}

const STUDENT_FIELDS = [
  "id",
  "user_id",
  "user:id",
  "user:firstname",
  "user:lastname",
  "user:email",
  "user:phone",
  "user:photo",
  "user:photo_thumb",
  "user:external_id",
  "user:register_date",
  "user:last_activity_date",
  "user:classgroups:id",
  "user:classgroups:name",
].join(",");

const ATTEMPT_FIELDS = [
  "attempts:id",
  "attempts:status",
  "attempts:credibility",
  "attempts:credibility_verdict",
].join(",");

const ASSIGNMENT_FIELDS =
  "id,name,status,is_proctoring,students_count,starting_at,webinar_id";


interface CacheEntry<T> {
  at: number;
  value: T;
}

const CACHE = new Map<string, CacheEntry<unknown>>();
const INFLIGHT = new Map<string, Promise<unknown>>();

export const GLOBAL_SEARCH_TTL = {
  search: 30_000,
  list: 120_000,
  detail: 60_000,
  count: 300_000,
  live: 20_000,
  liveNow: 2_000,
};

async function cached<T>(key: string, ttl: number, loader: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const hit = CACHE.get(key);

  if (hit && now - hit.at < ttl) return hit.value as T;

  const running = INFLIGHT.get(key);
  if (running) return running as Promise<T>;

  const promise = loader()
    .then((value) => {
      CACHE.set(key, { at: Date.now(), value });
      return value;
    })
    .finally(() => {
      INFLIGHT.delete(key);
    });

  INFLIGHT.set(key, promise);
  return promise;
}

const WARM_EXAM_LIMIT = 3;

export function dropGlobalSearchCache(): void {
  CACHE.clear();
}

export interface Page<T> {
  items: T[];
  hasMore: boolean;
}

const unwrapPage = <T,>(payload: unknown, requested: number): Page<T> => {
  const entities = (payload as { entities?: unknown } | null)?.entities;

  if (Array.isArray(entities)) {
    return { items: entities as T[], hasMore: entities.length >= requested };
  }

  const meta = entities as { data?: unknown; current_page?: unknown; last_page?: unknown } | null;
  const items = Array.isArray(meta?.data) ? (meta?.data as T[]) : [];

  const current = Number(meta?.current_page ?? 1);
  const last = Number(meta?.last_page ?? current);

  return {
    items,
    hasMore: Number.isFinite(current) && Number.isFinite(last)
      ? current < last
      : items.length >= requested,
  };
};

const unwrapList = <T,>(payload: unknown): T[] => {
  const entities = (payload as { entities?: unknown } | null)?.entities;
  if (Array.isArray(entities)) return entities as T[];

  const data = (entities as { data?: unknown } | null)?.data;
  if (Array.isArray(data)) return data as T[];

  return [];
};

interface RawAttempt {
  id: number | string;
  student_id?: number | string;
  status?: string | null;
  credibility?: number | null;
  credibility_verdict?: { credibility?: number | null } | null;
}

interface RawStudent {
  id: number;
  user_id?: number | null;
  attempts?: RawAttempt[] | null;
  user?: {
    id?: number | null;
    firstname?: string | null;
    lastname?: string | null;
    email?: string | null;
    phone?: string | null;
    photo?: string | null;
    photo_thumb?: { small?: string | null; medium?: string | null } | null;
    external_id?: string | number | null;
    register_date?: string | null;
    last_activity_date?: string | null;
    classgroups?: Array<{ id: number; name?: string | null }> | null;
  } | null;
}

interface RawAssignment {
  id: number;
  name?: string | null;
  status?: string | null;
  is_proctoring?: boolean | number | null;
  students_count?: number | string | null;
  starting_at?: string | null;
  webinar_id?: number | string | null;
}

interface RawGroup {
  id: number;
  name?: string | null;
}

const pickAttempt = (attempts: RawAttempt[] | null | undefined): RawAttempt | null => {
  if (!Array.isArray(attempts) || attempts.length === 0) return null;
  return attempts.find((attempt) => attempt.status === "active") ?? attempts.at(-1) ?? null;
};

const attemptScore = (attempt: RawAttempt | null, rowCredibility: number | null): number | null => {
  if (attempt) {
    if (typeof attempt.credibility === "number" && attempt.credibility >= 0) {
      return attempt.credibility;
    }

    const verdict = attempt.credibility_verdict?.credibility;
    return typeof verdict === "number" ? verdict : null;
  }

  return typeof rowCredibility === "number" ? rowCredibility : null;
};

const toStudentHit = (student: RawStudent & { credibility?: number | null }): StudentHit => {
  const user = student.user;
  const attempt = pickAttempt(student.attempts);
  const contact = user?.email || user?.phone || null;
  const externalId = user?.external_id != null ? String(user.external_id) : null;

  const hit: StudentHit = {
    id: student.id,
    userId:
      typeof user?.id === "number"
        ? user.id
        : typeof student.user_id === "number"
        ? student.user_id
        : null,
    name: [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim() || `#${student.id}`,
    detail: [contact, externalId].filter(Boolean).join(" · ") || null,
    photo: user?.photo_thumb?.small || user?.photo_thumb?.medium || user?.photo || null,
    groups: (user?.classgroups ?? [])
      .map((group) => group?.name)
      .filter((name): name is string => Boolean(name)),
    email: user?.email ?? null,
    phone: user?.phone ?? null,
    externalId,
    registerDate: user?.register_date ?? null,
    lastActivityDate: user?.last_activity_date ?? null,
    credibility: typeof student.credibility === "number" ? student.credibility : null,
    attemptId: attempt ? Number(attempt.id) : null,
    attemptCredibility: attemptScore(
      attempt,
      typeof student.credibility === "number" ? student.credibility : null
    ),
  };

  void rememberStudent(hit.id, hit.name, hit.photo, hit);

  return hit;
};

const toAssignmentHit = (assignment: RawAssignment): AssignmentHit => ({
  id: assignment.id,
  name: assignment.name || `#${assignment.id}`,
  status: assignment.status ?? null,
  isProctoring: Boolean(assignment.is_proctoring),
  studentsCount:
    assignment.students_count != null && Number.isFinite(Number(assignment.students_count))
      ? Number(assignment.students_count)
      : null,
  startingAt: assignment.starting_at ?? null,
  webinarId:
    assignment.webinar_id != null && Number.isFinite(Number(assignment.webinar_id))
      ? Number(assignment.webinar_id)
      : null,
});

export async function searchStudents(
  query: string,
  options: { assignmentId?: number; limit?: number } = {}
): Promise<StudentHit[]> {
  const { assignmentId, limit = 6 } = options;

  return cached(`students:${query}:${assignmentId ?? 0}:${limit}`, GLOBAL_SEARCH_TTL.search, async () => {
  const { data } = await foregroundGet("/students", {
    params: {
      query,
      limit,
      per_page: limit,
      ...(assignmentId ? { assignment_id: assignmentId } : {}),
    },
    headers: { "X-Requested-Fields": STUDENT_FIELDS },
  });

  return unwrapList<RawStudent>(data).map(toStudentHit);
  });
}

export async function searchAssignments(query: string, limit = 6): Promise<AssignmentHit[]> {
  return cached(`assignments:${query}:${limit}`, GLOBAL_SEARCH_TTL.search, async () => {
  const { data } = await foregroundGet("/assignments", {
    params: { query, limit, per_page: limit, orderBy: "desc" },
    headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
  });

  return unwrapList<RawAssignment>(data).map(toAssignmentHit);
  });
}

export async function searchGroups(query: string, limit = 5): Promise<GroupHit[]> {
  return cached(`groups:${query}:${limit}`, GLOBAL_SEARCH_TTL.search, async () => {
  const { data } = await foregroundGet("/classes", {
    params: { query, limit, per_page: limit },
    headers: { "X-Requested-Fields": "id,name" },
  });

  return unwrapList<RawGroup>(data).map((group) => ({
    id: group.id,
    name: group.name || `#${group.id}`,
  }));
  });
}

export async function fetchStudentById(
  studentId: number,
  assignmentId?: number
): Promise<StudentHit | null> {
  return cached(`student:${studentId}:${assignmentId ?? 0}`, GLOBAL_SEARCH_TTL.detail, async () => {
  const { data } = await backgroundGet("/students", {
    params: {
      "id[]": studentId,
      limit: 1,
      per_page: 1,
      ...(assignmentId ? { assignment_id: assignmentId } : {}),
    },
    headers: { "X-Requested-Fields": STUDENT_FIELDS },
  });

  const [student] = unwrapList<RawStudent>(data);
  return student ? toStudentHit(student) : null;
  });
}

export async function fetchStudentAssignments(
  studentId: number,
  limit = 12
): Promise<AssignmentHit[]> {
  return cached(`student-assignments:${studentId}:${limit}`, GLOBAL_SEARCH_TTL.detail, async () => {
  const { data } = await backgroundGet("/assignments", {
    params: { student_id: studentId, limit, per_page: limit, orderBy: "desc" },
    headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
  });

  return unwrapList<RawAssignment>(data).map(toAssignmentHit);
  });
}

const ASSIGNMENT_BY_ID_LIMIT = 8;

export async function fetchAssignmentsByIds(ids: number[]): Promise<AssignmentHit[]> {
  const wanted = [...new Set(ids)].slice(0, ASSIGNMENT_BY_ID_LIMIT);

  const loaded = await Promise.all(
    wanted.map((id) =>
      cached(`assignment:${id}`, GLOBAL_SEARCH_TTL.detail, async () => {
        const { data } = await backgroundGet(`/assignments/${id}`, {
          headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
        });

        const entity = (data as { entity?: RawAssignment } | null)?.entity;

        return entity ? toAssignmentHit(entity) : null;
      }).catch(() => null)
    )
  );

  return loaded.filter((item): item is AssignmentHit => item != null);
}

export async function fetchRunningProctoredAssignments(
  page = 1,
  limit = 25
): Promise<Page<AssignmentHit>> {
  return cached(`running:${limit}:${page}`, GLOBAL_SEARCH_TTL.list, async () => {
  const { data } = await backgroundGet("/assignments", {
    params: { proctoring: 1, status: "process", limit, per_page: limit, page, orderBy: "desc" },
    headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
  });

  const page_ = unwrapPage<RawAssignment>(data, limit);
  return { items: page_.items.map(toAssignmentHit), hasMore: page_.hasMore };
  });
}

export async function fetchAssignmentStudents(
  assignmentId: number,
  options: { byViolations?: boolean; limit?: number; page?: number } = {}
): Promise<Page<StudentHit>> {
  const { byViolations = false, limit = 25, page = 1 } = options;
  return cached(`assignment-students:${assignmentId}:${byViolations}:${limit}:${page}`, GLOBAL_SEARCH_TTL.detail, async () => {

  const { data } = await backgroundGet("/students", {
    params: {
      assignment_id: assignmentId,
      limit,
      per_page: limit,
      page,
      ...(byViolations ? { sortBy: "credibility", sort: "desc" } : {}),
    },
    headers: { "X-Requested-Fields": `${STUDENT_FIELDS},${ATTEMPT_FIELDS},credibility` },
  });

  const page_ = unwrapPage<RawStudent & { credibility?: number | null }>(data, limit);
  return { items: page_.items.map(toStudentHit), hasMore: page_.hasMore };
  });
}

export async function fetchAssignmentParticipantsCount(assignmentId: number): Promise<number | null> {
  return cached(`participants:${assignmentId}`, GLOBAL_SEARCH_TTL.count, async () => {
  const { data } = await backgroundGet("/students", {
    params: { assignment_id: assignmentId, limit: 1, per_page: 1 },
    headers: { "X-Requested-Fields": "id" },
  });

  const total = (data as { entities?: { total?: unknown } } | null)?.entities?.total;
  return typeof total === "number" ? total : null;
  });
}

export async function fetchGroupStudents(
  classId: number,
  page = 1,
  limit = 25
): Promise<Page<StudentHit>> {
  return cached(`group-students:${classId}:${limit}:${page}`, GLOBAL_SEARCH_TTL.detail, async () => {
  const { data } = await backgroundGet("/students", {
    params: { class_id: classId, limit, per_page: limit, page },
    headers: { "X-Requested-Fields": STUDENT_FIELDS },
  });

  const page_ = unwrapPage<RawStudent>(data, limit);
  return { items: page_.items.map(toStudentHit), hasMore: page_.hasMore };
  });
}

const USER_FIELDS =
  "id,firstname,lastname,email,phone,photo,photo_thumb,group,external_id,student:id";

interface RawUser {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phone?: string | null;
  photo?: string | null;
  photo_thumb?: { small?: string | null; medium?: string | null } | null;
  group?: string | null;
  external_id?: string | number | null;
  student?: { id?: number | null } | null;
}

export async function searchUsers(query: string, limit = 5): Promise<UserHit[]> {
  return cached(`users:${query}:${limit}`, GLOBAL_SEARCH_TTL.search, async () => {
  const { data } = await foregroundGet("/users", {
    params: { query, limit, per_page: limit },
    headers: { "X-Requested-Fields": USER_FIELDS },
  });

  return unwrapList<RawUser>(data).map((user) => ({
    id: user.id,
    name: [user.firstname, user.lastname].filter(Boolean).join(" ").trim() || `#${user.id}`,
    detail:
      [user.email || user.phone, user.external_id != null ? String(user.external_id) : null]
        .filter(Boolean)
        .join(" · ") || null,
    photo: user.photo_thumb?.small || user.photo_thumb?.medium || user.photo || null,
    role: user.group ?? null,
    studentId: typeof user.student?.id === "number" ? user.student.id : null,
  }));
  });
}

export async function fetchAllStudents(page = 1, limit = 25): Promise<Page<StudentHit>> {
  return cached(`all-students:${limit}:${page}`, GLOBAL_SEARCH_TTL.list, async () => {
  const { data } = await backgroundGet("/students", {
    params: { limit, per_page: limit, page, sortBy: "lastname", sort: "asc" },
    headers: { "X-Requested-Fields": STUDENT_FIELDS },
  });

  const page_ = unwrapPage<RawStudent>(data, limit);
  return { items: page_.items.map(toStudentHit), hasMore: page_.hasMore };
  });
}

export async function fetchAssignmentsByStatus(
  status: string,
  page = 1,
  limit = 25
): Promise<Page<AssignmentHit>> {
  return cached(`assignments-status:${status}:${limit}:${page}`, GLOBAL_SEARCH_TTL.list, async () => {
    const { data } = await backgroundGet("/assignments", {
      params: { status, limit, per_page: limit, page, orderBy: "desc" },
      headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
    });

    const page_ = unwrapPage<RawAssignment>(data, limit);
    return { items: page_.items.map(toAssignmentHit), hasMore: page_.hasMore };
  });
}

export async function fetchRecentAssignments(page = 1, limit = 25): Promise<Page<AssignmentHit>> {
  return cached(`recent-assignments:${limit}:${page}`, GLOBAL_SEARCH_TTL.list, async () => {
  const { data } = await backgroundGet("/assignments", {
    params: { limit, per_page: limit, page, orderBy: "desc" },
    headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
  });

  const page_ = unwrapPage<RawAssignment>(data, limit);
  return { items: page_.items.map(toAssignmentHit), hasMore: page_.hasMore };
  });
}

export async function fetchAllGroups(page = 1, limit = 25): Promise<Page<GroupHit>> {
  return cached(`all-groups:${limit}:${page}`, GLOBAL_SEARCH_TTL.list, async () => {
  const { data } = await backgroundGet("/classes", {
    params: { limit, per_page: limit, page },
    headers: { "X-Requested-Fields": "id,name" },
  });

  const page_ = unwrapPage<RawGroup>(data, limit);

  return {
    items: page_.items.map((group) => ({ id: group.id, name: group.name || `#${group.id}` })),
    hasMore: page_.hasMore,
  };
  });
}

const NON_VIOLATION_TYPES = new Set([
  "started",
  "opened",
  "closed",
  "finished",
  "answer_posted",
  "comment_posted",
  "head_restored",
  "focus_restored",
  "connection_restored",
  "bluetooth_off",
  "eye_passed",
  "object_passed",
]);

export interface ViolationSummary {
  type: string;
  count: number;
}

export interface AttemptSummary {
  id: number;
  number: number;
  status: string | null;
  points: number | null;
  credibility: number | null;
  spendingTime: number | null;
  createdAt: string | null;
}

const numberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export async function fetchAttemptList(
  assignmentId: number,
  studentId: number
): Promise<AttemptSummary[]> {
  return cached(`attempts:${assignmentId}:${studentId}`, GLOBAL_SEARCH_TTL.detail, async () => {
    const rows = (await queryStudentAttempts(assignmentId, studentId, {
      fields: ATTEMPT_LIST_FIELDS,
    }))
      .filter((row) => row.id != null && Number(row.student_id) === studentId)
      .sort((a, b) => {
        const left = a.created_at ? new Date(a.created_at).getTime() : 0;
        const right = b.created_at ? new Date(b.created_at).getTime() : 0;
        return left - right;
      });

    return rows.map((row, index) => ({
      id: Number(row.id),
      number: index + 1,
      status: row.status ?? null,
      points: numberOrNull(row.points),
      credibility: numberOrNull(row.credibility),
      spendingTime: numberOrNull(row.spending_time),
      createdAt: row.created_at ?? null,
    }));
  });
}

export async function fetchAttemptViolations(
  assignmentId: number,
  studentId: number,
  attemptId: number,
  limit = 200
): Promise<{ items: ViolationSummary[]; total: number }> {
  return cached(`violations:${assignmentId}:${studentId}:${attemptId}:${limit}`, GLOBAL_SEARCH_TTL.detail, async () => {
  const { data } = await backgroundGet(`/assignment/actions/${assignmentId}.json`, {
    params: {
      student_id: studentId,
      assignment_attempt_id: attemptId,
      is_warning: 1,
      limit,
      per_page: limit,
      orderBy: "desc",
    },
    headers: { "X-Requested-Fields": "id,action_type,assignment_attempt_id" },
  });

  const list = unwrapList<{ action_type?: string | null }>(data);
  const counts = new Map<string, number>();

  for (const action of list) {
    const type = action.action_type ?? "";
    if (!type || NON_VIOLATION_TYPES.has(type)) continue;
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  const items = [...counts.entries()]
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);

  const total = items.reduce((sum, item) => sum + item.count, 0);

  return { items, total };
  });
}

export type LiveState = "live" | "in-exam" | "away" | "finished" | "not-started" | "offline";

export type LiveSource = "janus" | "session" | "actions";

export interface LiveStatus {
  state: LiveState;
  offCameraState: LiveState;
  source: LiveSource;
  attemptId: number | null;
  cameraSince: string | null;
  lastSeen: string | null;
}

export const OFFLINE_STATUS: LiveStatus = {
  state: "offline",
  offCameraState: "offline",
  source: "actions",
  attemptId: null,
  cameraSince: null,
  lastSeen: null,
};

const PRESENCE_WINDOW_MS = 10 * 60 * 1000;
const ATTEMPT_OVER_TYPES = new Set(["finished", "closed"]);

interface RawWebinarSession {
  id: number;
  status?: string | null;
  created_at?: string | null;
}

interface RawAction {
  action_type?: string | null;
  created_at?: string | null;
}

async function fetchStudentSessions(
  assignmentId: number,
  studentId: number
): Promise<RawWebinarSession[]> {
  const { data } = await backgroundGet("/webinar-sessions", {
    params: { assignment_id: assignmentId, student_id: studentId, limit: 30, per_page: 30 },
    headers: { "X-Requested-Fields": "id,status,created_at" },
  });

  return unwrapList<RawWebinarSession>(data).filter((session) => session.status === "started");
}

async function fetchStudentAttempts(
  assignmentId: number,
  studentId: number
): Promise<RawAttempt[]> {
  const all = (await queryStudentAttempts(assignmentId, studentId, {
    fields: ATTEMPT_OWNER_FIELDS,
  })) as RawAttempt[];

  void rememberExam(
    assignmentId,
    null,
    null,
    all.filter(
      (attempt): attempt is RawAttempt & { id: number | string; student_id: number | string } =>
        attempt.id != null && attempt.student_id != null
    ),
    true
  );

  return all.filter((attempt) => Number(attempt.student_id) === studentId);
}

async function fetchLastAction(
  assignmentId: number,
  studentId: number,
  attemptId: number
): Promise<RawAction | null> {
  const { data } = await backgroundGet(`/assignment/actions/${assignmentId}.json`, {
    params: {
      student_id: studentId,
      assignment_attempt_id: attemptId,
      limit: 1,
      per_page: 1,
      orderBy: "desc",
    },
    headers: { "X-Requested-Fields": "id,action_type,created_at" },
  });

  return unwrapList<RawAction>(data)[0] ?? null;
}

export async function fetchLiveStatus(
  assignmentId: number,
  studentId: number
): Promise<LiveStatus> {
  return cached(`live:${assignmentId}:${studentId}`, GLOBAL_SEARCH_TTL.live, async () => {
    const [sessions, attempts] = await Promise.all([
      fetchStudentSessions(assignmentId, studentId).catch(() => []),
      fetchStudentAttempts(assignmentId, studentId).catch(() => []),
    ]);

    const attempt = attempts.find((item) => item.status === "active") ?? attempts.at(-1) ?? null;
    const attemptId = attempt ? Number(attempt.id) : null;

    const cameraSince = sessions
      .map((session) => session.created_at)
      .filter((value): value is string => Boolean(value))
      .sort()
      .at(-1) ?? null;

    if (attemptId == null) {
      return {
        state: "not-started",
        offCameraState: "not-started",
        source: "actions",
        attemptId: null,
        cameraSince: null,
        lastSeen: null,
      };
    }

    const action = await fetchLastAction(assignmentId, studentId, attemptId).catch(() => null);
    const lastSeen = action?.created_at ?? null;
    const seenAt = lastSeen ? new Date(lastSeen).getTime() : NaN;
    const isFresh = !Number.isNaN(seenAt) && Date.now() - seenAt < PRESENCE_WINDOW_MS;

    const offCameraState: LiveState =
      action?.action_type && ATTEMPT_OVER_TYPES.has(action.action_type)
        ? "finished"
        : isFresh
        ? "in-exam"
        : "away";

    const live = sessions.length > 0;

    return {
      state: live ? "live" : offCameraState,
      offCameraState,
      source: live ? "session" : "actions",
      attemptId,
      cameraSince: live ? cameraSince : null,
      lastSeen: lastSeen ?? cameraSince,
    };
  });
}

export async function fetchPresence(
  assignmentId: number,
  studentId: number,
  userId: number | null,
  examOpen: boolean
): Promise<LiveStatus> {
  const base = await fetchLiveStatus(assignmentId, studentId);

  if (!examOpen) return base;

  const online = await sweepAssignment(assignmentId).catch(() => null);
  if (!online) return base;

  const onCamera = online.has(studentId);

  return {
    ...base,
    state: onCamera ? "live" : base.offCameraState,
    source: "janus",
    cameraSince: onCamera ? base.cameraSince : null,
  };
}

let warmedAt = 0;

const USED_KEY = "te-global-search-used";
const WARMED_KEY = "te-global-search-warmed-at";

function readWarmedAt(): number {
  if (typeof window === "undefined") return 0;

  try {
    return Number(window.localStorage.getItem(WARMED_KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function writeWarmedAt(at: number): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(WARMED_KEY, String(at));
  } catch {
    return;
  }
}

export function markGlobalSearchUsed(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(USED_KEY, "1");
  } catch {
    return;
  }
}

function wasGlobalSearchUsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(USED_KEY) === "1";
  } catch {
    return false;
  }
}

export async function warmGlobalSearch(options: { onlyIfUsed?: boolean } = {}): Promise<void> {
  if (typeof window === "undefined") return;
  if (document.hidden) return;
  if (options.onlyIfUsed && !wasGlobalSearchUsed()) return;
  if (Date.now() - Math.max(warmedAt, readWarmedAt()) < GLOBAL_SEARCH_TTL.list) return;

  warmedAt = Date.now();
  writeWarmedAt(warmedAt);

  const lists = await Promise.allSettled([
    fetchRunningProctoredAssignments(1),
    fetchRecentAssignments(1),
    fetchAllStudents(1),
    fetchAllGroups(1),
  ]);

  const running = lists[0];
  if (running.status !== "fulfilled") return;

  await Promise.allSettled(
    running.value.items
      .slice(0, WARM_EXAM_LIMIT)
      .map((assignment) => fetchAssignmentStudents(assignment.id, { page: 1 }))
  );
}

export function forgetWarmup(): void {
  warmedAt = 0;
  writeWarmedAt(0);
}

export interface LiveNowEntry {
  assignmentId: number | null;
  assignmentName: string;
  student: StudentHit;
  webinarId: number | null;
  onCamera: boolean;
  inRoom: boolean;
  confirmed: boolean;
}

export interface LiveNow {
  entries: LiveNowEntry[];
  liveStudentIds: Set<number>;
  liveUserIds: Set<number>;
  examByStudentId: Record<number, string>;
  scanned: number;
  total: number;
}

export const EMPTY_LIVE_NOW: LiveNow = {
  entries: [],
  liveStudentIds: new Set(),
  liveUserIds: new Set(),
  examByStudentId: {},
  scanned: 0,
  total: 0,
};

const LIVE_NOW_KEY = "live-now";

export function dropLiveNowCache(): void {
  CACHE.delete(LIVE_NOW_KEY);
  dropLiveRoomsCache();
}

async function findStudentLiveExam(
  studentId: number,
  webinarId: number | null
): Promise<AssignmentHit | null> {
  return cached(`student-live-exam:${studentId}`, GLOBAL_SEARCH_TTL.live, async () => {
    const { data } = await backgroundGet("/assignments", {
      params: { student_id: studentId, proctoring: 1, limit: 10, per_page: 10 },
      headers: { "X-Requested-Fields": ASSIGNMENT_FIELDS },
    });

    const items = unwrapList<RawAssignment>(data).map(toAssignmentHit);

    if (webinarId != null) {
      const exact = items.find((item) => item.webinarId === webinarId);
      if (exact) return exact;
    }

    return items.find((item) => item.status === "process") ?? null;
  });
}

export async function resolveLiveExam(entry: LiveNowEntry): Promise<AssignmentHit | null> {
  if (entry.assignmentId != null) {
    return {
      id: entry.assignmentId,
      name: entry.assignmentName,
      status: "process",
      isProctoring: true,
      studentsCount: null,
      startingAt: null,
      webinarId: entry.webinarId,
    };
  }

  return findStudentLiveExam(entry.student.id, entry.webinarId);
}

const STUDENT_CARD_FRESH_MS = 15 * 60 * 1000;

async function fetchStudentsByIds(ids: number[]): Promise<StudentHit[]> {
  if (ids.length === 0) return [];

  const fresh: StudentHit[] = [];
  const missing: number[] = [];

  for (const id of ids) {
    const cached = STUDENT_CARDS.get(id);

    if (cached && Date.now() - cached.at < STUDENT_CARD_FRESH_MS) {
      fresh.push(cached.hit);
      continue;
    }

    missing.push(id);
  }

  if (missing.length === 0) return fresh;

  const byField = async (field: "id[]" | "user_id[]", ids: number[]): Promise<StudentHit[]> => {
    if (ids.length === 0) return [];

    const params = new URLSearchParams();
    params.set("limit", String(ids.length));
    params.set("per_page", String(ids.length));
    ids.forEach((id) => params.append(field, String(id)));

    const { data } = await backgroundGet(`/students?${params.toString()}`, {
      headers: { "X-Requested-Fields": STUDENT_FIELDS },
    });

    return unwrapList<RawStudent>(data).map(toStudentHit);
  };

  const loaded = await byField("id[]", missing);
  const found = new Set(loaded.map((hit) => hit.id));

  // В display комнаты может лежать не id студента, а id пользователя — SDK ставит туда
  // разные идентификаторы. Кого не нашли по одному полю, ищем по второму.
  const unresolved = missing.filter((id) => !found.has(id));
  const byUser = unresolved.length > 0 ? await byField("user_id[]", unresolved).catch(() => []) : [];

  const all = [...loaded, ...byUser];

  for (const hit of all) {
    STUDENT_CARDS.set(hit.id, { at: Date.now(), hit });
    if (hit.userId != null) STUDENT_CARDS.set(hit.userId, { at: Date.now(), hit });
  }

  if (unresolved.length > 0) {
  }

  return [...fresh, ...all];
}

const STUDENT_CARDS = new Map<number, { at: number; hit: StudentHit }>();

export async function fetchLiveNow(): Promise<LiveNow> {
  return cached(LIVE_NOW_KEY, GLOBAL_SEARCH_TTL.liveNow, async () => {
    const rooms = await fetchLiveRooms().catch(() => [] as LiveRoom[]);

    if (rooms.length === 0) return EMPTY_LIVE_NOW;

    const sweep = await sweepLiveRooms(rooms).catch(() => null);
    const roomState = readRoomState();

    if (sweep === null && roomState.size === 0) {
      return { ...EMPTY_LIVE_NOW, total: rooms.length };
    }

    const byExam = new Map<number, RoomSweep>();

    for (const item of sweep ?? []) byExam.set(item.assignmentId, item);

    // Комната, к которой страница прокторинга подключена живьём, знает правду точнее опроса:
    // её ответ перекрывает результат прохода, включая случай «там уже никого».
    for (const [assignmentId, studentIds] of roomState) {
      const known = rooms.find((room) => room.assignmentId === assignmentId);

      byExam.set(assignmentId, {
        assignmentId,
        assignmentName: known?.assignmentName ?? byExam.get(assignmentId)?.assignmentName ?? null,
        webinarId: known?.webinarId ?? byExam.get(assignmentId)?.webinarId ?? null,
        studentIds,
      });
    }

    const examByStudent = new Map<number, RoomSweep>();

    for (const item of byExam.values()) {
      for (const studentId of item.studentIds) {
        if (!examByStudent.has(studentId)) examByStudent.set(studentId, item);
      }
    }

    const studentIds = [...examByStudent.keys()];

    if (studentIds.length === 0) {
      return { ...EMPTY_LIVE_NOW, scanned: rooms.length, total: rooms.length };
    }

    const students = await fetchStudentsByIds(studentIds).catch(() => [] as StudentHit[]);
    const byId = new Map(students.map((student) => [student.id, student]));

    const result: LiveNow = {
      entries: [],
      liveStudentIds: new Set<number>(),
      liveUserIds: new Set<number>(),
      examByStudentId: {},
      scanned: rooms.length,
      total: rooms.length,
    };

    for (const [studentId, room] of examByStudent) {
      const student = byId.get(studentId);

      if (!student) {
        continue;
      }

      result.entries.push({
        assignmentId: room.assignmentId,
        assignmentName: room.assignmentName ?? "",
        student,
        webinarId: room.webinarId,
        onCamera: true,
        inRoom: true,
        confirmed: true,
      });

      result.liveStudentIds.add(student.id);
      if (student.userId != null) result.liveUserIds.add(student.userId);
      if (room.assignmentName) result.examByStudentId[student.id] = room.assignmentName;
    }

    return result;
  });
}
